import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { EmptyStatesDoqContainer } from '@ardoq/graph';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { Network3DEmptyReason } from './sourceData/buildNetwork3DSourceData';

export type Network3DEmptyStateProps = {
  emptyReason: Network3DEmptyReason;
  i18nLocale: I18nLocale;
};

/**
 * One state, unlike the XYZ charts' four.
 *
 * Those four need a field selection before they can draw anything, so most of their empty states
 * are about fields: none picked, none numeric, or every component excluded for having no value.
 * This view needs no fields - it draws the graph it was handed - so the only way it has nothing to
 * show is having no components, and the remedy is the traversal rather than anything in the view.
 *
 * Deliberately not an empty state for "no references". A viewpoint of components with no
 * references between them is a real picture: the radial tree and the bundled sphere are entirely
 * about containment, and even the force layout says something true about an unconnected estate.
 */
export const Network3DEmptyState = ({
  emptyReason,
  i18nLocale,
}: Network3DEmptyStateProps) => {
  if (emptyReason !== 'no components') return null;

  return (
    <EmptyStatesDoqContainer>
      <ResponsiveDoqWithSpeechBubble
        doqType={DoqType.EMPTY_BOX}
        title={t(
          i18nLocale,
          'views:network3d.emptyState.noComponents.title',
          'No components to show'
        )}
        message={t(
          i18nLocale,
          'views:network3d.emptyState.noComponents.message',
          'This viewpoint loaded no components. Adjust the traversal to include some, and their references will be drawn between them.'
        )}
      />
    </EmptyStatesDoqContainer>
  );
};
